import React, { useRef, useState, useEffect, useCallback } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { useHistory } from "react-router-dom";
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { BrCalendar } from './BrCalendar';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ErrorValidationMessage } from './ErrorValidationMessage';

import api from '../api/api';

import '../layout/sass/_form.scss';
import { Dropdown } from 'primereact/dropdown';

export const FormLicenca = ({ isEditing, isLoading, defaultValues }) => {
    
    const [alunos, setAlunos] = useState([]);
    const history = useHistory();
    const toast = useRef(null);
    const [isSubmiting, setIsSubmiting] = useState(false)
    
    const schema = Yup.object().shape({
        alunoId: Yup
            .number()
            .required("O aluno é obrigatório")
            .positive("O aluno deve ser selecionado")
            .integer("O aluno deve estar na lista"),        
        obs: Yup
            .string(),
        dataInicio: Yup
            .date()
            .typeError("Uma data deve ser inserida")
            .required("A data de inicio é obrigatória"),
        dataFim: Yup
            .date()
            .min(Yup.ref('dataInicio'), "A data de fim deve ser maior ou igual à data de início")
            .typeError("Uma data deve ser inserida")
            .required("A data de fim é obrigatória"),     
        tipo: Yup
            .string()
            .required("O tipo é obrigatório")
    })

    const formik = useFormik({

        initialValues: { ...defaultValues, alunoId: defaultValues?.alunoId || '' },

        validationSchema: schema,

        enableReinitialize: true,

        onSubmit: async (data) => {
            setIsSubmiting(true)      
        
            const payload = {
                ...data,
                dataInicio: data.dataInicio ? new Date(data.dataInicio).toISOString() : null,
                dataFim: data.dataFim ? new Date(data.dataFim).toISOString() : null,
            };

            try {
                if (!isEditing) {
                    await api.post("licenca", payload)

                    formik.resetForm()
                    toast.current.show({ life: 2000, severity: 'success', summary: 'Cadastro concluído!', detail: `Licença adicionada com sucesso` });
                    setTimeout(async () => {
                        await deleteFalta(payload);
                        history.push('/licenca')

                    }, 2000)

                }
                else {
                    await api.put("licenca", payload)
                    
                    toast.current.show({ life: 2000, severity: 'success', summary: 'Cadastro atualizado!', detail: `Licença atualizada com sucesso` });
                    setTimeout(() => {
                        
                        history.push('/licenca')

                    }, 2000)
                }
            } catch (error) {
                console.error("Erro ao salvar/atualizar licença:", error); 
                const errorMsg = error.response?.data?.message || error.toString();
                toast.current.show({ life: 5000, severity: 'error', summary: `Erro ao ${isEditing ? 'editar' : 'cadastrar'} licença!`, detail: errorMsg });
            } finally { 
                setIsSubmiting(false);
            }
        }
    })

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    
    const deleteFalta = async (licencaData) => {
        if (!licencaData || !licencaData.alunoId || !licencaData.dataInicio || !licencaData.dataFim) {
            console.error("Dados da licença incompletos para deleteFalta:", licencaData);
            return;
        }

        const alunoId = licencaData.alunoId;
        const licencaInicio = new Date(licencaData.dataInicio);
        const licencaFim = new Date(licencaData.dataFim);
        licencaFim.setHours(23, 59, 59, 999); 

        try {
            const matriculasResponse = await api.get(`matricula/vigente/${alunoId}`);
            const matriculasVigentes = matriculasResponse.data;

            if (!matriculasVigentes || matriculasVigentes.length === 0) {
                console.log(`Nenhuma matrícula vigente encontrada para aluno ${alunoId}`);
                return;
            }

            for (const matricula of matriculasVigentes) {
                const matriculaIdEspecifica = matricula.id;
                if (!matriculaIdEspecifica) continue; 

                try {
                    const faltasResponse = await api.get(`falta/porMatricula/${matriculaIdEspecifica}`);
                    const faltasDaMatricula = faltasResponse.data;

                    for (const falta of faltasDaMatricula) {
                        const dataOcorrencia = new Date(falta.dataOcorrencia + 'T00:00:00');

                        if (dataOcorrencia >= licencaInicio && dataOcorrencia <= licencaFim) {
                            console.log(`Falta ${falta.id} (Matrícula ${matriculaIdEspecifica}) dentro do período. Deletando...`);

                            try {
                                const faltasSeguidasResponse = await api.get(`falta/faltasSeguidas/${matriculaIdEspecifica}`);
                                const faltasSeguidas = faltasSeguidasResponse.data;

                                if (faltasSeguidas.length === 3 && falta.id === faltasSeguidas[0].id && faltasSeguidas[0].matriculaStatus === "DESISTENTE") {

                                    const statusHistoricoResponse = await api.get(`historicoStatus/matricula/${matriculaIdEspecifica}`);
                                    const statusAnterior = statusHistoricoResponse.data?.[0]?.status;

                                    if (statusAnterior && statusAnterior !== "DESISTENTE") {
                                        console.log(`Revertendo status da Matrícula ${matriculaIdEspecifica} para ${statusAnterior}`);
                                        const matriculaAtualResponse = await api.get(`matricula/${matriculaIdEspecifica}`);
                                        const matriculaAtual = matriculaAtualResponse.data;
                                        
                                        const matriculaUpdatePayload = {
                                            id: matriculaAtual.id,
                                            conclusao: matriculaAtual.conclusao,
                                            dataMatricula: matriculaAtual.dataMatricula,
                                            notaFinal: matriculaAtual.notaFinal,
                                            status: statusAnterior, 
                                            alunoId: matriculaAtual.alunoId,
                                            instrumentoId: matriculaAtual.instrumentoId,
                                            periodoId: matriculaAtual.periodoId,
                                            turmaId: matriculaAtual.turmaId,
                                        };
                                        await api.put("matricula", matriculaUpdatePayload);
                                    } else {
                                         console.warn(`Não foi possível reverter status da Matrícula ${matriculaIdEspecifica}. Histórico não encontrado ou status anterior também era DESISTENTE.`);
                                    }
                                }
                            } catch (errorStatus) {
                                console.error(`Erro ao verificar/reverter status para Matrícula ${matriculaIdEspecifica} antes de deletar Falta ${falta.id}:`, errorStatus);
                            }

                            await api.delete(`falta/${falta.id}`);
                            toast.current.show({ life: 1500, severity: 'info', summary: 'Falta Removida', detail: `Falta do dia ${dataOcorrencia.toLocaleDateString()} removida devido à licença.` });
                        }
                    }
                } catch (errorFaltas) {
                    console.error(`Erro ao buscar ou deletar faltas para Matrícula ${matriculaIdEspecifica}:`, errorFaltas);
                }
            }
        } catch (errorMatriculas) {
            console.error(`Erro ao buscar matrículas para Aluno ${alunoId}:`, errorMatriculas);
            toast.current.show({ severity: 'error', summary: 'Erro ao buscar matrículas do aluno', detail: errorMatriculas.toString() });
        }
    };
    const getAlunos = useCallback(async () => {
        try {
            const response = await api.get(`perfil/perfilAll`);

            setAlunos(response.data.map(perfil => ({
                label: `${perfil.nome} ${perfil.cpf ? `- ${perfil.cpf}` : ''}`, 
                value: perfil.id
            })));
        }
        catch (error) {
            console.error("Erro ao obter alunos:", error);
            toast.current.show({ severity: 'error', summary: 'Erro ao obter Alunos', detail: error.toString() });
        }
    }, []);

    useEffect(() => {
        getAlunos(); 
    }, [getAlunos]);

    return (
        <>
        <div className="card p-grid p-col-12 p-mx-0 p-mt-0">
            <Toast ref={toast} position="bottom-right" />
            <div className="p-col-12">
                <h2 className="p-mt-0">Licença</h2>
                <form onSubmit={formik.handleSubmit}>
                    <div className="p-formgrid p-grid p-fluid p-mx-0">
                    <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="alunoId"
                                className={classNames({ 'p-error': isFormFieldValid('alunoId') })}
                            >
                                Aluno
                            </label>
                            {
                                !isLoading ?
                                <Dropdown
                                    id="alunoId"
                                    name="alunoId"
                                    options={alunos}
                                    emptyMessage="Nenhum aluno encontrado"
                                    emptyFilterMessage={"Nenhum aluno encontrado"}
                                    filter={true}
                                    filterBy="label"
                                    value={formik.values.alunoId}
                                    onChange={(e) => formik.setFieldValue("alunoId", e.value)}
                                    className={classNames({ 'p-invalid': isFormFieldValid('alunoId') })}
                                    placeholder="Selecione um aluno"
                                    showClear={true} 
                                />  
                                    :
                                    <Skeleton height="35px" />
                            }
                            {formik.errors.alunoId && formik.touched.alunoId &&
                                <ErrorValidationMessage message={formik.errors.alunoId} />
                            }
                        </div>
                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="obs"
                                className={classNames({ 'p-error': isFormFieldValid('obs') })}
                            >
                                Observação
                            </label>
                            {
                                !isLoading ?
                                    <InputText
                                        id="obs"
                                        name="obs"
                                        className={classNames({ 'p-invalid': isFormFieldValid('obs') })}
                                        value={formik.values.obs}
                                        onChange={formik.handleChange}
                                    />
                                    :
                                    <Skeleton height="35px" />
                            }
                            {formik.errors.obs && formik.touched.obs &&
                                <ErrorValidationMessage message={formik.errors.obs} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4 p-md-4">
                                <label
                                    htmlFor="dataInicio"
                                    className={classNames({ 'p-error': isFormFieldValid('dataInicio') })}
                                >
                                    Data Inicio
                                </label>
                                <BrCalendar
                                    id="dataInicio"
                                    name="dataInicio"
                                    value={formik.values.dataInicio}
                                    onChange={formik.handleChange}
                                    placeholder={ isEditing ? new Date(formik.values.dataInicio).toLocaleDateString('pt-BR') : "" }
                                    className={classNames({ 'p-invalid': isFormFieldValid('dataInicio') })}
                                    yearRange={`2000:${new Date().getFullYear()}`}
                                />
                                {formik.errors.dataInicio && formik.touched.dataInicio &&
                                    <ErrorValidationMessage message={formik.errors.dataInicio} />
                                }
                        </div>

                        <div className="p-field p-col-12 p-sm-4 p-md-4">
                                <label
                                    htmlFor="dataFim"
                                    className={classNames({ 'p-error': isFormFieldValid('dataFim') })}
                                >
                                    Data Fim
                                </label>
                                <BrCalendar
                                    id="dataFim"
                                    name="dataFim"
                                    value={formik.values.dataFim}
                                    onChange={formik.handleChange}
                                    placeholder={ isEditing ? new Date(formik.values.dataFim).toLocaleDateString('pt-BR') : ""}
                                    className={classNames({ 'p-invalid': isFormFieldValid('dataFim') })}
                                    yearRange={`2000:${new Date().getFullYear()}`}
                                />
                                {formik.errors.dataFim && formik.touched.dataFim &&
                                    <ErrorValidationMessage message={formik.errors.dataFim} />
                                }
                        </div>


                        <div className="p-field p-col-12 p-sm-4">
                            <label
                                htmlFor="tipo"
                                className={classNames({ 'p-error': isFormFieldValid('tipo') })}
                            >
                                Tipo
                            </label>
                            {
                                !isLoading ?
                                    <Dropdown
                                        id="tipo"
                                        name="tipo"
                                        value={formik.values.tipo}
                                        options={formik.values.tipos}
                                        className={classNames({ 'p-invalid': isFormFieldValid('tipo') })}
                                        onChange={formik.handleChange}
                                    /> 
                                    :
                                    <Skeleton height="35px" />
                            }
                            {formik.errors.tipo && formik.touched.tipo &&
                                <ErrorValidationMessage message={formik.errors.tipo} />
                            }
                        </div>

                    </div>
                    <div className="p-d-flex p-jc-end p-mr-1">
                        {
                            !isLoading ?
                                <Button
                                    label={isEditing ? 'Atualizar' : 'Salvar'}
                                    type="submit"
                                    iconPos="right"
                                    loading={isSubmiting}
                                />
                                :
                                <Skeleton width="82.16px" height="35px" />

                        }
                    </div>
                </form>
            </div>
        </div>
        
    </>
    );
}